import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { standardCodeLogic } from "utils/codes";
import slabCodeTemplate from "features/Model/Enclosure/Templates/slabCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
// import { LayerGrade } from "components/EmbodiedCarbonGrade";

import globalStyles from "components/globalStyles.module.scss";

import WarningIcon from "assets/images/icons/WarningOrange.svg";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const framingValidation = getValidation("csSlabFraming");
const spacingValidation = getValidation("csSlabSpacing");
const insValidation = getValidation("csSlabInsulation");
const interiorValidation = getValidation("csSlabIntFinish");
const sheathingValidation = getValidation("csSlabSheathing");

const StandardFloorsAddedCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};

        const uid = useStateSelector(`user.uid`);
        const { slabInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { codes: modelCodes = {}, components = {} } = useStateSelector(`model.modelData`) || {};

        const { value: framingValue } = useStateSelector(`${drawerAccessor}.slabCode.layers.framing`) || {};
        const { value: spacingValue } = useStateSelector(`${drawerAccessor}.slabCode.layers.spacing`) || {};
        const { value: insulationValue } = useStateSelector(`${drawerAccessor}.slabCode.layers.slabInsulation`) || {};
        const { value: interiorFinishValue } =
            useStateSelector(`${drawerAccessor}.slabCode.layers.interiorFinish`) || {};
        const { value: sheathingValue } = useStateSelector(`${drawerAccessor}.slabCode.layers.sheathing`) || {};
        const slabLabel = useStateSelector(`${drawerAccessor}.slabCode.label`) || "";

        const slabValue = `${framingValue}${spacingValue}${insulationValue}${interiorFinishValue}${sheathingValue}`;

        const { codeRef: editingCodeRef = null, isLibCode = false } = slabInsType;
        const isEditing = !!editingCodeRef;

        const valueRef = useRef(null);

        useEffect(() => {
            const initialValues = {
                slabCode: isEmpty(slabInsType) ? slabCodeTemplate : slabInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = slabValue;
        }, [slabValue, slabLabel]);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Slab (Floors Added) Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"} style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.slabCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={slabLabel.toString() === valueRef.current ? slabValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.slabCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={slabValue}
                    />
                    {!isCodeLibrary && (
                        <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                            <CodeLibraryDestination
                                drawerAccessor={drawerAccessor}
                                editingCodeRef={editingCodeRef}
                                isLibCode={isLibCode}
                            />
                        </div>
                    )}
                </InputRow>
                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.slabCode.layers.framing`}
                            label="Framing"
                            placeholder="Choose Framing"
                            options={getOptions({ fieldKey: "csSlabFraming" })}
                            validate={framingValidation}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.slabCode.layers.spacing`}
                            label="Spacing"
                            placeholder="Choose Spacing"
                            options={getOptions({ fieldKey: "csSlabSpacing" })}
                            validate={spacingValidation}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                </div>
                <InputRow
                    gridTemplate="1fr auto"
                    style={{
                        border: "1px solid #E0E0E0",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gridGap: "0",
                        marginBottom: "4px",
                    }}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.slabCode.layers.slabInsulation`}
                        label="Insulation"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csSlabInsulation" })}
                        validate={insValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                    {/* <LayerGrade
                        layerType="insulationLayer1"
                        code={insulationValue}
                        withBorder={true}
                        borderHeight="36px"
                        customGap="0.5rem"
                    /> */}
                </InputRow>
                <InputRow
                    gridTemplate="1fr auto"
                    style={{
                        border: "1px solid #E0E0E0",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gridGap: "0",
                        marginBottom: "4px",
                    }}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.slabCode.layers.interiorFinish`}
                        label="Interior Finish"
                        placeholder="Choose Interior Finish"
                        options={getOptions({ fieldKey: "csSlabIntFinish" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                    {/* <LayerGrade
                        layerType="interior"
                        code={interiorFinishValue}
                        withBorder={true}
                        borderHeight="36px"
                        customGap="0.5rem"
                    /> */}
                </InputRow>
                <InputRow
                    gridTemplate="1fr auto"
                    style={{
                        border: "1px solid #E0E0E0",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gridGap: "0",
                    }}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.slabCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csSlabSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                    {/* <LayerGrade
                        layerType="sheathing"
                        code={sheathingValue}
                        withBorder={true}
                        borderHeight="36px"
                        customGap="0.5rem"
                    /> */}
                </InputRow>
                <div className={globalStyles.codeDrawerWarning}>
                    <img src={WarningIcon} alt="Warning" />
                    <p>Embodied carbon calculation is not supported for Floors Added codes.</p>
                </div>

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.slabCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "slabInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const { slabCode = {}, addToLibrary = false, orgLibraryId = "", selectedComponents = [] } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: "FloorsAdded",
            componentId,
            code: slabCode,
            inputName: "slabInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardFloorsAddedCode);
